import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { postContact } from "../lib/api";
import { useT } from "../lib/i18n";

export default function Contact() {
    const { t, lang } = useT();
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        topic: t.contact.topics[0],
        message: "",
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        document.title = lang === "it" ? "Contatti, Casati Consulting" : "Contact, Casati Consulting";
        // Reset topic when language changes
        setForm((f) => ({ ...f, topic: t.contact.topics[0] }));
    }, [lang, t.contact.topics]);

    function update(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function onSubmit(e) {
        e.preventDefault();
        if (busy) return;
        if (form.name.trim().length < 2 || !form.email.includes("@") || form.message.trim().length < 10) {
            toast.error(t.contact.invalid);
            return;
        }
        setBusy(true);
        try {
            await postContact({ ...form, language: lang });
            setDone(true);
            toast.success(t.contact.success);
        } catch {
            toast.error(t.contact.error);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="bg-[#F9F9F7] text-[#1C1C1C]">
            <Toaster position="bottom-center" richColors closeButton />
            <section className="pt-20 md:pt-28 pb-8">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                    <p className="eyebrow">{t.contact.eyebrow}</p>
                    <h1
                        className="font-display mt-6 text-5xl sm:text-7xl lg:text-[100px] tracking-tight font-medium leading-[0.92]"
                        style={{ letterSpacing: "-0.035em" }}
                        data-testid="contact-title"
                    >
                        {t.contact.titleA}
                        <br />
                        <span className="italic font-light">{t.contact.titleB}</span>
                    </h1>
                </div>
            </section>

            <section className="py-16 md:py-24 border-t hair">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-y-12 md:gap-x-12">
                    <aside className="col-span-12 md:col-span-4 space-y-10">
                        <div>
                            <p className="eyebrow">{t.common.office}</p>
                            <p className="mt-4 leading-relaxed">
                                Casati Consulting
                                <br />
                                {t.common.byAppointment}
                                <br />
                                Swiss Life Select
                                <br />
                                Europastrasse 18
                                <br />
                                8152 Glattbrugg
                            </p>
                        </div>
                        <div>
                            <p className="eyebrow">{t.common.email}</p>
                            <a
                                href="mailto:matteo.casati@swisslife-select.ch"
                                className="link-underline mt-4 inline-block"
                                data-testid="contact-email"
                            >
                                matteo.casati@swisslife-select.ch
                            </a>
                        </div>
                        <div>
                            <p className="eyebrow">{t.common.phone}</p>
                            <a
                                href="tel:+41775013627"
                                className="link-underline mt-4 inline-block"
                                data-testid="contact-phone-display"
                            >
                                +41 77 501 36 27
                            </a>
                        </div>
                        <div>
                            <p className="eyebrow">{t.common.languages}</p>
                            <p className="mt-4">{t.common.languagesValue}</p>
                        </div>
                        <div>
                            <p className="eyebrow">{t.common.responseTime}</p>
                            <p className="mt-4 text-[#1C1C1C]/70 leading-relaxed">{t.common.responseBody}</p>
                        </div>
                        <p
                            className="text-sm italic leading-relaxed border-l-2 pl-4 py-1"
                            style={{ borderColor: "#9B7A58", color: "#1C1C1C" }}
                            data-testid="contact-free-consultation"
                        >
                            {t.services.freeConsultation}
                        </p>
                    </aside>

                    <div className="col-span-12 md:col-span-8">
                        {done ? (
                            <div
                                className="border hair p-10 md:p-14 bg-white flex flex-col items-start gap-5"
                                data-testid="contact-success"
                            >
                                <CheckCircle2 size={36} strokeWidth={1.5} className="text-[#9B7A58]" />
                                <h2 className="font-display text-3xl md:text-4xl tracking-tight font-medium" style={{ letterSpacing: "-0.02em" }}>
                                    {t.contact.successTitle}
                                </h2>
                                <p className="text-[#1C1C1C]/75 leading-relaxed max-w-md">
                                    {t.contact.successBody}{" "}
                                    <a href="mailto:matteo.casati@swisslife-select.ch" className="link-underline">
                                        matteo.casati@swisslife-select.ch
                                    </a>
                                    .
                                </p>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setDone(false);
                                        setForm({ name: "", email: "", phone: "", topic: t.contact.topics[0], message: "" });
                                    }}
                                    className="mt-4 text-[12px] uppercase tracking-[0.22em] font-bold link-underline"
                                    data-testid="contact-success-reset"
                                >
                                    {t.cta.another}
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={onSubmit} className="space-y-10" data-testid="contact-form" noValidate>
                                <Field label={t.contact.f.name} required>
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={(e) => update("name", e.target.value)}
                                        className="cf-input"
                                        autoComplete="name"
                                        required
                                        minLength={2}
                                        data-testid="contact-name"
                                    />
                                </Field>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                    <Field label={t.contact.f.email} required>
                                        <input
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => update("email", e.target.value)}
                                            className="cf-input"
                                            autoComplete="email"
                                            required
                                            data-testid="contact-email-input"
                                        />
                                    </Field>
                                    <Field label={t.contact.f.phone}>
                                        <input
                                            type="tel"
                                            value={form.phone}
                                            onChange={(e) => update("phone", e.target.value)}
                                            className="cf-input"
                                            autoComplete="tel"
                                            data-testid="contact-phone"
                                        />
                                    </Field>
                                </div>
                                <Field label={t.contact.f.topic} required>
                                    <select
                                        value={form.topic}
                                        onChange={(e) => update("topic", e.target.value)}
                                        className="cf-input bg-transparent"
                                        data-testid="contact-topic"
                                    >
                                        {t.contact.topics.map((tp) => (
                                            <option key={tp} value={tp}>{tp}</option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label={t.contact.f.message} required>
                                    <textarea
                                        rows={5}
                                        value={form.message}
                                        onChange={(e) => update("message", e.target.value)}
                                        className="cf-input resize-none"
                                        required
                                        minLength={10}
                                        data-testid="contact-message"
                                    />
                                </Field>

                                <div className="flex items-center justify-between gap-6">
                                    <p className="text-xs text-[#1C1C1C]/55 max-w-md leading-relaxed">{t.contact.consent}</p>
                                    <button
                                        type="submit"
                                        disabled={busy}
                                        className="inline-flex items-center gap-2 px-7 h-12 bg-[#1C1C1C] text-[#F9F9F7] text-[12px] uppercase tracking-[0.22em] font-bold hover:bg-[#9B7A58] disabled:opacity-50 transition-colors"
                                        data-testid="contact-submit"
                                    >
                                        {busy ? (
                                            <>
                                                <Loader2 size={14} className="animate-spin" />
                                                {t.contact.sending}
                                            </>
                                        ) : (
                                            t.contact.send
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            <style>{`
                .cf-input {
                    width: 100%;
                    background: transparent;
                    border: 0;
                    border-bottom: 1.5px solid rgba(28,28,28,0.18);
                    padding: 12px 0;
                    font-size: 16px;
                    color: #1C1C1C;
                    outline: none;
                    transition: border-color .25s ease;
                    font-family: inherit;
                }
                .cf-input:focus { border-bottom-color: #1C1C1C; }
                .cf-input::placeholder { color: rgba(28,28,28,0.35); }
            `}</style>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "#9B7A58" }}>
                {label}
                {required && <span className="ml-1 text-[#1C1C1C]/40">*</span>}
            </span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
